import { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { ModalDeclineAction } from "components/modals/ModalDeclineAction"
import { UserKycTable } from "components/tables/UserKycTable"

import { kycService } from "services/kyc/kyc.service"

import { CardWrapper } from "../cards/CardWrapper"
import { UserProfileHeader } from "../cards/UserProfileHeader"

interface UserKycDocsProps {
  userResult: any
  handleToggleExclusion: () => void
  updateKey: number
  handleAddBonusSuccess: () => void
  setOpenAddBonus: (value: boolean) => void
  setOpenSendMessage: (value: boolean) => void
}

export const UserKycDocs = ({
  userResult,
  handleToggleExclusion,
  updateKey,
  handleAddBonusSuccess,
  setOpenAddBonus,
  setOpenSendMessage
}: UserKycDocsProps) => {
  if (!userResult) return null

  const { t } = useTranslation("translation", { useSuspense: false })
  const [approveRow, setApproveRow] = useState<any>(null)
  const [declineRow, setDeclineRow] = useState<any>(null)
  const [returnToPendingRow, setReturnToPendingRow] = useState<any>(null)

  const [kycData, setKycData] = useState<any>(null)

  useEffect(() => {
    const userId = userResult?.id
    if (!userId) return

    getKYCFilesData(userId)
    checkDuplicate(userId)
  }, [userResult?.id])

  const checkDuplicate = async (user_id: string) => {
    const { duplicate } = await kycService.getDuplicateRecord(user_id)
    if (duplicate) toast.info("Duplicates detected")
  }

  const getKYCFilesData = async (user_id: string) => {
    const { data } = await kycService.getKYCFiles(user_id)
    if (data) setKycData(data)
  }

  const onApprove = async (row: any) => {
    try {
      await kycService.updateKYCFilesStatus(row?.id, "COMPLETED")
      toast.success("KYC Approved")
      if (userResult?.id) getKYCFilesData(userResult.id)
    } catch (e: any) {
      toast.error(e.message || "Failed to approve KYC")
    }
  }

  const onDecline = async (kycId: string, reason: string) => {
    try {
      await kycService.updateKYCFilesStatus(kycId, "DECLINED", reason)
      toast.success("KYC Declined")
      if (userResult?.id) getKYCFilesData(userResult.id)
    } catch (e: any) {
      toast.error(e.message || "Failed to decline KYC")
    }
  }

  const onReturnToPending = async (row: any) => {
    try {
      await kycService.updateKYCFilesStatus(row?.id, "PENDING")
      toast.success("KYC status set to Pending")
      if (userResult?.id) getKYCFilesData(userResult.id)
    } catch (e: any) {
      toast.error(e.message || "Failed to update KYC status")
    }
  }

  return (
    <div className="w-full flex flex-col sm:grid grid-col-1 gap-8">
      <UserProfileHeader
        userResult={userResult}
        handleToggleExclusion={handleToggleExclusion}
        updateKey={updateKey}
        handleAddBonusSuccess={handleAddBonusSuccess}
        setOpenAddBonus={setOpenAddBonus}
        setOpenSendMessage={setOpenSendMessage}
      />
      <CardWrapper>
        <div className="flex justify-between items-center mb-4">
          <div className="text-2xl">{t("user.kycFiles")}</div>
          <div className="flex justify-end gap-4">
            <button className="btn btn-secondary" onClick={() => setDeclineRow(kycData)}>
              {t("common.decline")}
            </button>
            <button className="btn btn-primary" onClick={() => setApproveRow(kycData)}>
              {t("common.approve")}
            </button>
            {["COMPLETED", "DECLINED"].includes(kycData?.status) && (
              <button className="btn btn-outline" onClick={() => setReturnToPendingRow(kycData)}>
                {t("user.returnToPending")}
              </button>
            )}
          </div>
        </div>

        <UserKycTable kycData={kycData} />
      </CardWrapper>

      <ModalConfirmAction
        data={approveRow}
        handler={() => setApproveRow(null)}
        text={`Are you sure you want to approve ${approveRow?.user?.username || "this user"}?`}
        actionText="Approve"
        actionHandler={onApprove}
      />

      <ModalConfirmAction
        data={returnToPendingRow}
        handler={() => setReturnToPendingRow(null)}
        text={`Are you sure you want to return ${userResult?.user?.username || "this user"} to pending state?`}
        actionText="Set Pending"
        actionHandler={onReturnToPending}
      />

      <ModalDeclineAction
        data={declineRow}
        handler={() => setDeclineRow(null)}
        text={`Are you sure you want to decline ${declineRow?.user?.username || "this user"}?`}
        actionText="Decline"
        actionHandler={onDecline}
      />
    </div>
  )
}
